import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FolderKanban, Plus, Search, Clock, DollarSign, Calendar,
  MoreVertical, PlayCircle, PauseCircle, CheckCircle, BarChart3,
  Timer, TrendingUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const Projects = () => {
  const { token, loading: authLoading, user } = useAuth();
  const [activeTab, setActiveTab] = useState('projects');
  const [projects, setProjects] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isProjectDialogOpen, setIsProjectDialogOpen] = useState(false);
  const [isTimeDialogOpen, setIsTimeDialogOpen] = useState(false);
  const [projectForm, setProjectForm] = useState({
    name: '',
    description: '',
    customer_name: '',
    start_date: '',
    end_date: '',
    budget: 0,
    hourly_rate: 0,
    status: 'active'
  });
  const [timeForm, setTimeForm] = useState({
    project_id: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    hours: 0,
    billable: true,
    notes: ''
  });

  useEffect(() => {
    if (!authLoading && token) {
      fetchProjects();
      fetchTimeEntries();
    } else if (!authLoading && !token) {
      setLoading(false);
    }
  }, [token, authLoading]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeEntries = async () => {
    try {
      const response = await axios.get(`${API}/time-entries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTimeEntries(response.data);
    } catch (error) {
      console.error('Failed to fetch time entries');
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/projects`, projectForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Project created');
      setIsProjectDialogOpen(false);
      fetchProjects();
      resetProjectForm();
    } catch (error) {
      toast.error('Error creating project');
    }
  };

  const handleCreateTimeEntry = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/time-entries`, timeForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Time entry recorded');
      setIsTimeDialogOpen(false);
      fetchTimeEntries();
      fetchProjects();
      resetTimeForm();
    } catch (error) {
      toast.error('Error recording time');
    }
  };

  const handleUpdateProjectStatus = async (projectId, status) => {
    try {
      const project = projects.find(p => p.id === projectId);
      await axios.patch(`${API}/projects/${projectId}`, { ...project, status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Project status updated');
      fetchProjects();
    } catch (error) {
      toast.error('Error updating project');
    }
  };

  const resetProjectForm = () => {
    setProjectForm({
      name: '',
      description: '',
      customer_name: '',
      start_date: '',
      end_date: '',
      budget: 0,
      hourly_rate: 0,
      status: 'active'
    });
  };

  const resetTimeForm = () => {
    setTimeForm({
      project_id: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      hours: 0,
      billable: true,
      notes: ''
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-emerald-500/20 text-emerald-400';
      case 'on_hold': return 'bg-amber-500/20 text-amber-400';
      case 'completed': return 'bg-blue-500/20 text-blue-400';
      case 'cancelled': return 'bg-red-500/20 text-red-400';
      default: return 'bg-slate-500/20 text-slate-400';
    }
  };

  const totalHours = timeEntries.reduce((sum, e) => sum + e.hours, 0);
  const billableAmount = timeEntries.filter(e => e.billable).reduce((sum, e) => sum + (e.amount || 0), 0);
  const activeProjects = projects.filter(p => p.status === 'active').length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Projects & Time Tracking</h1>
          <p className="text-slate-400">Manage projects and track billable hours</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-500/20 rounded-lg">
                <FolderKanban className="w-5 h-5 text-indigo-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Total Projects</p>
                <p className="text-xl font-bold text-white">{projects.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/20 rounded-lg">
                <PlayCircle className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Active</p>
                <p className="text-xl font-bold text-white">{activeProjects}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <Clock className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Total Hours</p>
                <p className="text-xl font-bold text-white">{totalHours.toFixed(1)}h</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <DollarSign className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Billable Amount</p>
                <p className="text-xl font-bold text-white">MYR {billableAmount.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800/50 border-slate-700">
          <TabsTrigger value="projects" className="data-[state=active]:bg-indigo-600">
            Projects
          </TabsTrigger>
          <TabsTrigger value="time" className="data-[state=active]:bg-indigo-600">
            Time Entries
          </TabsTrigger>
        </TabsList>

        <TabsContent value="projects" className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-slate-800/50 border-slate-700 text-white"
              />
            </div>
            <Dialog open={isProjectDialogOpen} onOpenChange={setIsProjectDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="w-4 h-4 mr-2" />
                  New Project
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-900 border-slate-700">
                <DialogHeader>
                  <DialogTitle className="text-white">Create New Project</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateProject} className="space-y-4">
                  <div>
                    <Label className="text-slate-300">Project Name *</Label>
                    <Input
                      value={projectForm.name}
                      onChange={(e) => setProjectForm({...projectForm, name: e.target.value})}
                      className="bg-slate-800 border-slate-700 text-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Description</Label>
                    <Textarea
                      value={projectForm.description}
                      onChange={(e) => setProjectForm({...projectForm, description: e.target.value})}
                      className="bg-slate-800 border-slate-700 text-white"
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Customer</Label>
                    <Input
                      value={projectForm.customer_name}
                      onChange={(e) => setProjectForm({...projectForm, customer_name: e.target.value})}
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Start Date *</Label>
                      <Input
                        type="date"
                        value={projectForm.start_date}
                        onChange={(e) => setProjectForm({...projectForm, start_date: e.target.value})}
                        className="bg-slate-800 border-slate-700 text-white"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">End Date</Label>
                      <Input
                        type="date"
                        value={projectForm.end_date}
                        onChange={(e) => setProjectForm({...projectForm, end_date: e.target.value})}
                        className="bg-slate-800 border-slate-700 text-white"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Budget (MYR)</Label>
                      <Input
                        type="number"
                        value={projectForm.budget}
                        onChange={(e) => setProjectForm({...projectForm, budget: parseFloat(e.target.value) || 0})}
                        className="bg-slate-800 border-slate-700 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Hourly Rate (MYR)</Label>
                      <Input
                        type="number"
                        value={projectForm.hourly_rate}
                        onChange={(e) => setProjectForm({...projectForm, hourly_rate: parseFloat(e.target.value) || 0})}
                        className="bg-slate-800 border-slate-700 text-white"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setIsProjectDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                      Create Project
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="text-center py-10">
              <div className="animate-spin w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full mx-auto"></div>
            </div>
          ) : projects.length === 0 ? (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-10 text-center">
                <FolderKanban className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                <p className="text-slate-400">No projects found</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {projects
                .filter(p => p.name?.toLowerCase().includes(searchTerm.toLowerCase()))
                .map((project) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-indigo-500/20 rounded-xl flex items-center justify-center">
                            <FolderKanban className="w-6 h-6 text-indigo-400" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-white">{project.name}</h3>
                              <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusColor(project.status)}`}>
                                {project.status}
                              </span>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-slate-400 mt-1">
                              {project.customer_name && <span>{project.customer_name}</span>}
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {project.start_date}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {project.total_hours || 0}h logged
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          {project.budget > 0 && (
                            <div className="text-right">
                              <p className="text-sm text-slate-400">Budget</p>
                              <p className="font-semibold text-white">MYR {project.budget?.toLocaleString()}</p>
                            </div>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="w-4 h-4 text-slate-400" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="bg-slate-800 border-slate-700">
                              <DropdownMenuItem 
                                className="text-slate-300"
                                onClick={() => {
                                  setTimeForm({...timeForm, project_id: project.id});
                                  setIsTimeDialogOpen(true);
                                }}
                              >
                                <Timer className="w-4 h-4 mr-2" />
                                Log Time
                              </DropdownMenuItem>
                              {project.status === 'active' && (
                                <DropdownMenuItem 
                                  className="text-amber-400"
                                  onClick={() => handleUpdateProjectStatus(project.id, 'on_hold')}
                                >
                                  <PauseCircle className="w-4 h-4 mr-2" />
                                  Put On Hold
                                </DropdownMenuItem>
                              )}
                              {project.status === 'on_hold' && (
                                <DropdownMenuItem 
                                  className="text-emerald-400"
                                  onClick={() => handleUpdateProjectStatus(project.id, 'active')}
                                >
                                  <PlayCircle className="w-4 h-4 mr-2" />
                                  Resume
                                </DropdownMenuItem>
                              )}
                              {project.status !== 'completed' && (
                                <DropdownMenuItem 
                                  className="text-blue-400"
                                  onClick={() => handleUpdateProjectStatus(project.id, 'completed')}
                                >
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Mark Complete
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="time" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={isTimeDialogOpen} onOpenChange={setIsTimeDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-600 hover:bg-indigo-700" disabled={projects.length === 0}>
                  <Plus className="w-4 h-4 mr-2" />
                  Log Time
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-900 border-slate-700">
                <DialogHeader>
                  <DialogTitle className="text-white">Log Time Entry</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateTimeEntry} className="space-y-4">
                  <div>
                    <Label className="text-slate-300">Project *</Label>
                    <Select 
                      value={timeForm.project_id}
                      onValueChange={(value) => setTimeForm({...timeForm, project_id: value})}
                    >
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                        <SelectValue placeholder="Select project" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        {projects.filter(p => p.status === 'active').map(p => (
                          <SelectItem key={p.id} value={p.id} className="text-white">
                            {p.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-300">Description *</Label>
                    <Input
                      value={timeForm.description}
                      onChange={(e) => setTimeForm({...timeForm, description: e.target.value})}
                      className="bg-slate-800 border-slate-700 text-white"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Date *</Label>
                      <Input
                        type="date"
                        value={timeForm.date}
                        onChange={(e) => setTimeForm({...timeForm, date: e.target.value})}
                        className="bg-slate-800 border-slate-700 text-white"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Hours *</Label>
                      <Input
                        type="number"
                        step="0.25"
                        value={timeForm.hours}
                        onChange={(e) => setTimeForm({...timeForm, hours: parseFloat(e.target.value) || 0})}
                        className="bg-slate-800 border-slate-700 text-white"
                        required
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={timeForm.billable}
                      onChange={(e) => setTimeForm({...timeForm, billable: e.target.checked})}
                      className="rounded border-slate-700"
                    />
                    <Label className="text-slate-300">Billable</Label>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setIsTimeDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                      Log Time
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {timeEntries.length === 0 ? (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-10 text-center">
                <Clock className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                <p className="text-slate-400">No time entries found</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {timeEntries.map((entry) => (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                            <Clock className="w-5 h-5 text-amber-400" />
                          </div>
                          <div>
                            <h4 className="font-medium text-white">{entry.description}</h4>
                            <div className="flex items-center gap-3 text-sm text-slate-400">
                              <span>{entry.date}</span>
                              <span>{entry.user_name}</span>
                              {entry.billable && (
                                <span className="px-2 py-0.5 rounded-full text-xs bg-emerald-500/20 text-emerald-400">
                                  Billable
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-white">{entry.hours}h</p>
                          {entry.billable && entry.amount > 0 && (
                            <p className="text-sm text-emerald-400">MYR {entry.amount?.toLocaleString()}</p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Projects;
